import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { DwComponent, DwComponentType, DwLoginComponent, DwMessage, DwMessageSeverity, DwRoutingService, DwSecurityState, DwSecurityStateService } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { IntegrationUtilService } from '../../../integration/services/integration-util.service';
import { PaymentsRepositoryService } from '../../../payments/services/payments-repository.service';
import { StripeSessionDetailsResult } from '../../../payments/models/stripe-session-details-result.model';
import { SchoolRepositoryService } from '../../../schools/services/school-repository.service';
import { BrandingPreviewPayload } from '../../../integration/models/branding-preview-payload.model';
import { AviatorRegistrationDraftService } from '../../services/aviator-registration-draft.service';

/** localStorage key for the "Remember me" username. The username is the only
 *  credential persisted - passwords stay with the browser's password manager. */
const rememberedUserNameStorageKey = 'aoLoginRememberedUserName';

@DwComponent({
  key: 'aviatorLogin',
  name: 'Aviator Login Page',
  componentType: DwComponentType.pageComponent,
  isGlobal: true
})
@Component({
  selector: 'app-aviator-login-page',
  templateUrl: './aviator-login-page.component.html',
  styleUrls: ['./aviator-login-page.component.scss']
})
export class AviatorLoginPageComponent implements OnInit, AfterViewInit, OnDestroy {
  topMessage: DwMessage;
  logoutMessageShown: boolean = false;
  stripeSessionDetails: StripeSessionDetailsResult;

  loading: boolean = true;

  loadingMessage: string = "loading...";;
  schoolDisplayName: string = 'AviatorPro';
  schoolFeaturesLoaded: boolean = false;
  hasThemedAuthSideImage: boolean = false;
  rememberMe: boolean = false;
  private brandingPreviewPayload: BrandingPreviewPayload;
  private loginComponent: DwLoginComponent;
  private loginDomObserver: MutationObserver;
  private loginDomCleanup: Array<() => void> = [];
  private schoolFeaturesSubscription: Subscription;

  /* dw-login lives behind *ngIf="!loading", so capture it through a setter and
     prefill the remembered username once it renders. */
  @ViewChild('loginRef')
  set loginComponentRef(loginComponent: DwLoginComponent) {
    this.loginComponent = loginComponent;
    this.prefillRememberedUserName();
  }

  constructor(protected integrationUtilService: IntegrationUtilService,
    protected dwSecurityStateService: DwSecurityStateService,
    protected paymentsRepositoryService: PaymentsRepositoryService,
    protected dwRoutingService: DwRoutingService,
    protected schoolRepositoryService: SchoolRepositoryService,
    protected aviatorRegistrationDraftService: AviatorRegistrationDraftService,
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.rememberMe = !!this.getRememberedUserName();
    this.integrationUtilService.setupIntegrationTokens();
    this.applyBrandingPreview();
    this.syncAuthSideImage();

    this.loadSchoolDisplayName();

    const fspToken = this.integrationUtilService.getIntegrationTokenDetails();

    if (fspToken) {
      this.topMessage = {
        messageBody: 'Login or Sign up to complete FSP integration',
        severity: DwMessageSeverity.info,
      }
    }

    const stripeToken = this.integrationUtilService.getStripeToken();

    this.dwSecurityStateService.securityStateObservable
      .subscribe(value => {
        this.handleSecurityChange(value);
      });

    this.handleSecurityChange(this.dwSecurityStateService.securityState);

    if (stripeToken) {
      this.loadStripeSession(stripeToken);
      return;
    }

    const joinRequestPayload = this.integrationUtilService.getJoinRequestPayload();

    if (joinRequestPayload) {
       this.topMessage = {
        messageBody: `Please sign up to complete your join request for Flight School ${joinRequestPayload.flightSchoolName}`,
        severity: DwMessageSeverity.info,
      }
    }

    this.loading = false;
  }

  ngAfterViewInit(): void {
    this.syncLoginFormEnhancements();

    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.loginDomObserver = new MutationObserver(() => this.syncLoginFormEnhancements());
    this.loginDomObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  ngOnDestroy(): void {
    if (this.schoolFeaturesSubscription) {
      this.schoolFeaturesSubscription.unsubscribe();
    }

    if (this.loginDomObserver) {
      this.loginDomObserver.disconnect();
    }

    this.loginDomCleanup.forEach(cleanup => cleanup());
    this.loginDomCleanup = [];
  }

  /* dw-login owns the form internals, so the page enhances them after render:
     reveal toggles on password inputs and the injected options row. */
  syncLoginFormEnhancements(): void {
    this.syncResetPasswordFields();
    this.syncPasswordRevealToggles();
    this.syncLoginOptionsRow();
  }

  private loadSchoolDisplayName(): void {
    this.schoolFeaturesSubscription = this.schoolRepositoryService.getSchoolFeatures()
      .subscribe(schoolFeatures => {
        const flightSchoolName = schoolFeatures?.FlightSchoolName?.trim();

        this.schoolDisplayName = this.brandingPreviewPayload?.schoolDisplayName?.trim() || flightSchoolName || 'AviatorPro';
        this.syncAuthSideImage();
        this.schoolFeaturesLoaded = true;
      }, () => {
        this.schoolDisplayName = this.brandingPreviewPayload?.schoolDisplayName?.trim() || 'AviatorPro';
        this.syncAuthSideImage();
        this.schoolFeaturesLoaded = true;
      });
  }

  protected syncAuthSideImage(): void {
    if (this.brandingPreviewPayload) {
      this.hasThemedAuthSideImage = !!this.brandingPreviewPayload.authSideImageUrl;
      return;
    }

    const themedImageUrl = document.documentElement.style.getPropertyValue('--dw-logo-url').trim();

    this.hasThemedAuthSideImage = !!themedImageUrl;
  }

  private applyBrandingPreview(): void {
    this.brandingPreviewPayload = this.integrationUtilService.getBrandingPreviewPayload();

    if (!this.brandingPreviewPayload) {
      return;
    }

    this.setPreviewCssVariable('--dw-navbar-bg', this.brandingPreviewPayload.topNavigationBackgroundColor);
    this.setPreviewCssVariable('--btn-primary-bg', this.brandingPreviewPayload.topNavigationBackgroundColor);
    this.setPreviewCssVariable('--btn-primary-color', this.brandingPreviewPayload.topNavigationTextColor);
    this.setPreviewCssVariable('--dw-sidebar-bg', this.brandingPreviewPayload.sidebarBackgroundColor);
    this.setPreviewCssVariable('--dw-sidebar-color', this.brandingPreviewPayload.sidebarTextColor);

    this.setPreviewCssVariable('--dw-logo-alt1-url', this.asCssUrl(this.brandingPreviewPayload.loginLogoUrl));
    this.setPreviewCssVariable('--dw-logo-login-url', this.asCssUrl(this.brandingPreviewPayload.loginLogoUrl));
    this.setPreviewCssVariable('--dw-logo-alt1-width', this.asPixelValue(this.brandingPreviewPayload.loginLogoWidth));
    this.setPreviewCssVariable('--dw-logo-alt1-height', this.asPixelValue(this.brandingPreviewPayload.loginLogoHeight));
    this.setPreviewCssVariable('--dw-logo-login-width', this.asPixelValue(this.brandingPreviewPayload.loginLogoWidth));
    this.setPreviewCssVariable('--dw-logo-login-height', this.asPixelValue(this.brandingPreviewPayload.loginLogoHeight));
    this.setPreviewCssVariable('--dw-auth-side-image-url', this.asCssUrl(this.brandingPreviewPayload.authSideImageUrl));
  }

  private setPreviewCssVariable(name: string, value?: string): void {
    if (!value) {
      return;
    }

    document.documentElement.style.setProperty(name, value);
  }

  private asCssUrl(url?: string): string {
    return url ? `url('${url}')` : '';
  }

  private asPixelValue(value?: number): string {
    return value > 0 ? `${value}px` : '';
  }

  syncPasswordRevealToggles(): void {
    const passwordInputs = Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLInputElement>(
        '.ao-login-form-panel input[type="password"], .ao-login-form-panel input[data-ao-password-reveal="true"]'
      )
    );

    passwordInputs
      .filter(input => this.shouldAddPasswordRevealToggle(input))
      .forEach(input => this.addPasswordRevealToggle(input));
  }

  private loadStripeSession(stripeToken: string) {
    this.loadingMessage = "Completing Purchase...";

    this.paymentsRepositoryService.getStripeSessionDetails(stripeToken)
      .subscribe(stripeSessionDetails => {
        this.loading= false;
        this.stripeSessionDetails = stripeSessionDetails;
        this.setupStripeSession(stripeSessionDetails);
      })
  }

  setupStripeSession(stripeSessionDetails: StripeSessionDetailsResult) {
    this.stripeSessionDetails = stripeSessionDetails;

    if (!stripeSessionDetails || !stripeSessionDetails.Success) {
      return;
    }

    if (stripeSessionDetails.IsExistingUser) {
      this.topMessage = {
        messageBody: `Purchase Complete!  Login with your existing account to access ${stripeSessionDetails.Products}`,
        severity: DwMessageSeverity.info,
      }
    } else {
      this.topMessage = {
        messageBody: `Purchase Complete!  Login or Sign up to access ${stripeSessionDetails.Products}`,
        severity: DwMessageSeverity.info,
      }
    }

  }

  goToRegister(): void {
    this.integrationUtilService.navigateToAuthPath('/register');
  }

  goToLogin(): void {
    this.integrationUtilService.navigateToAuthPath('/login');
  }



  rememberMeChanged(checked: boolean): void {
    this.rememberMe = checked;

    if (!checked) {
      this.clearRememberedUserName();
      return;
    }

    this.saveRememberedUserName();
  }

  private prefillRememberedUserName(): void {
    const rememberedUserName = this.getRememberedUserName();

    if (!this.loginComponent || !rememberedUserName || this.loginComponent.userName) {
      return;
    }

    // Defer so the prefill doesn't mutate dw-login's bindings mid change-detection
    Promise.resolve().then(() => this.loginComponent.userName = rememberedUserName);
  }

  private saveRememberedUserName(): void {
    if (!this.rememberMe) {
      return;
    }

    const userName = (this.loginComponent?.userName || '').trim();

    if (!userName) {
      return;
    }

    try {
      localStorage.setItem(rememberedUserNameStorageKey, userName);
    } catch {
      // Storage unavailable (private mode / blocked) - remember me just won't stick
    }
  }

  private getRememberedUserName(): string {
    try {
      return localStorage.getItem(rememberedUserNameStorageKey) || '';
    } catch {
      return '';
    }
  }

  private clearRememberedUserName(): void {
    try {
      localStorage.removeItem(rememberedUserNameStorageKey);
    } catch {
      // Storage unavailable - nothing to clear
    }
  }

  private handleSecurityChange(securityState: DwSecurityState) {

    if (securityState.isAuthenticated) {
      this.saveRememberedUserName();
      const authenticatedUser = securityState.securityContext?.AuthenticatedUser;
      const email = (authenticatedUser?.Email || this.loginComponent?.userName || '').trim();
      const userName = (authenticatedUser?.UserName || this.loginComponent?.userName || '').trim();

      this.aviatorRegistrationDraftService.updateDraftAccountEmail(email, userName);
    }

    if (securityState.inactivityLogoutComplete && !this.logoutMessageShown) {
      //this.dwSecurityStateService.setInactivityDetails(false, false, null);

      this.logoutMessageShown = true;


      this.topMessage = {
        severity: DwMessageSeverity.warning,
        messageBody: securityState.inactivityLogoutMessage,
        allowClose: true,
      }
    }
  }

  private shouldAddPasswordRevealToggle(input: HTMLInputElement): boolean {
    const inputName = (input.getAttribute('name') || '').toLowerCase();
    const inputStyle = input.getAttribute('style') || '';

    return input.dataset.aoPasswordReveal !== 'true'
      && !inputName.startsWith('fake')
      && inputStyle.indexOf('display: none') < 0;
  }

  private syncResetPasswordFields(): void {
    const resetForm = this.elementRef.nativeElement.querySelector<HTMLFormElement>(
      '.ao-login-form-panel dw-user-password-reset form'
    );

    if (!resetForm) {
      return;
    }

    this.setInputAutocomplete(resetForm.querySelector<HTMLInputElement>('#tbResetCode'), 'one-time-code');
    this.setInputAutocomplete(resetForm.querySelector<HTMLInputElement>('#tbUserName'), 'section-reset username');

    Array.from(resetForm.querySelectorAll<HTMLInputElement>('#tbPasswordNew, #tbPasswordNew2, #tbPasswordRetype'))
      .forEach(input => this.normalizeResetPasswordInput(input));
  }

  private setInputAutocomplete(input: HTMLInputElement | null, autocomplete: string): void {
    if (!input) {
      return;
    }

    this.renderer.setAttribute(input, 'autocomplete', autocomplete);
  }

  private normalizeResetPasswordInput(input: HTMLInputElement): void {
    this.renderer.setAttribute(input, 'autocomplete', 'section-reset new-password');
    this.renderer.setAttribute(input, 'autocapitalize', 'none');
    this.renderer.setAttribute(input, 'spellcheck', 'false');
    this.renderer.setAttribute(input, 'data-lpignore', 'true');
    this.renderer.setAttribute(input, 'data-1p-ignore', 'true');
    this.renderer.setAttribute(input, 'data-form-type', 'other');

    if (input.dataset.aoResetPasswordField === 'true') {
      return;
    }

    this.renderer.setAttribute(input, 'data-ao-reset-password-field', 'true');

    if (input.value) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  private addPasswordRevealToggle(input: HTMLInputElement): void {
    const inputContainer = input.closest('.p-float-label') || input.parentElement;

    if (!inputContainer) {
      return;
    }

    this.renderer.setAttribute(input, 'data-ao-password-reveal', 'true');
    this.renderer.addClass(inputContainer, 'ao-password-field');

    const toggle = this.renderer.createElement('button') as HTMLButtonElement;
    const icon = this.renderer.createElement('i') as HTMLElement;

    this.renderer.setAttribute(toggle, 'type', 'button');
    this.renderer.setAttribute(toggle, 'class', 'ao-password-toggle');
    this.renderer.setAttribute(toggle, 'aria-label', 'Show password');
    this.renderer.setAttribute(toggle, 'aria-pressed', 'false');
    this.renderer.setAttribute(toggle, 'title', 'Show password');
    this.renderer.setAttribute(icon, 'class', 'fa fa-eye');
    this.renderer.setAttribute(icon, 'aria-hidden', 'true');

    this.renderer.appendChild(toggle, icon);
    this.renderer.appendChild(inputContainer, toggle);

    const cleanup = this.renderer.listen(toggle, 'click', () => {
      const showPassword = input.type === 'password';

      input.type = showPassword ? 'text' : 'password';
      this.renderer.setAttribute(toggle, 'aria-label', showPassword ? 'Hide password' : 'Show password');
      this.renderer.setAttribute(toggle, 'aria-pressed', showPassword ? 'true' : 'false');
      this.renderer.setAttribute(toggle, 'title', showPassword ? 'Hide password' : 'Show password');
      this.renderer.setAttribute(icon, 'class', showPassword ? 'fa fa-eye-slash' : 'fa fa-eye');
      input.focus();
    });
    const focusCleanup = this.renderer.listen(inputContainer, 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target?.closest('.ao-password-toggle')) {
        return;
      }

      input.focus();
    });

    this.loginDomCleanup.push(cleanup, focusCleanup);
  }

  /* Injects a single options row - "Remember me" left, "Forgot your
     password?" right - directly under the password field and retires the
     framework's forgot-password row. Only applies to the main login state -
     recovery, 2FA and forced password change keep their stock rows ("Resend
     Code" reuses the same .input-additional-message structure). */
  private syncLoginOptionsRow(): void {
    const login = this.loginComponent;

    if (!login || login.showReset || login.showTwoFactor || login.showForcePasswordChange) {
      return;
    }

    const form = this.elementRef.nativeElement.querySelector<HTMLFormElement>('.ao-login-form-panel .login form');

    if (!form) {
      return;
    }

    Array.from(form.querySelectorAll('.input-additional-message')).forEach(message => {
      const forgotRow = message.closest('.row');

      if (forgotRow) {
        this.renderer.addClass(forgotRow, 'ao-login-forgot-row-replaced');
      }
    });

    if (form.querySelector('.ao-login-remember')) {
      return;
    }

    const passwordInput = form.querySelector<HTMLInputElement>(
      'input[type="password"], input[data-ao-password-reveal="true"]'
    );
    const passwordRow = passwordInput ? passwordInput.closest('.row') : null;

    if (!passwordRow) {
      return;
    }

    /* Wrapped in a .row so it inherits the enterprise layout sheet's
       "margin-left/right: 10px !important" row insets and stays aligned
       with the field edges. */
    const row = this.renderer.createElement('div') as HTMLDivElement;
    const label = this.renderer.createElement('label') as HTMLLabelElement;
    const checkbox = this.renderer.createElement('input') as HTMLInputElement;
    const text = this.renderer.createElement('span') as HTMLSpanElement;
    const forgotLink = this.renderer.createElement('button') as HTMLButtonElement;

    this.renderer.setAttribute(row, 'class', 'row ao-login-options-row');
    this.renderer.setAttribute(label, 'class', 'ao-login-remember');
    this.renderer.setAttribute(checkbox, 'type', 'checkbox');
    this.renderer.setAttribute(checkbox, 'class', 'ao-login-remember__checkbox');
    this.renderer.setAttribute(forgotLink, 'type', 'button');
    this.renderer.setAttribute(forgotLink, 'class', 'ao-login-forgot-link');
    checkbox.checked = this.rememberMe;

    this.renderer.appendChild(text, this.renderer.createText('Remember me'));
    this.renderer.appendChild(label, checkbox);
    this.renderer.appendChild(label, text);
    this.renderer.appendChild(forgotLink, this.renderer.createText('Forgot your password?'));
    this.renderer.appendChild(row, label);
    this.renderer.appendChild(row, forgotLink);
    this.renderer.insertBefore(form, row, passwordRow.nextSibling);

    const checkboxCleanup = this.renderer.listen(checkbox, 'change', () => this.rememberMeChanged(checkbox.checked));
    const forgotCleanup = this.renderer.listen(forgotLink, 'click', () => this.loginComponent?.forgotPassword());

    this.loginDomCleanup.push(checkboxCleanup, forgotCleanup);
  }

}
